<script lang="ts" setup>
import { z } from 'zod';
import { resolveAuthenticatedLoginRedirect, resolveSafeRedirectTarget } from '~/utils/authRedirect';
import { clearPrivateRuntimeCaches } from '~/lib/pwa/authCache.mjs';
import { runLoginFlow } from '~/utils/authFlow';
definePageMeta({
  title: 'Login',
  layout: 'non-logged'
});

const { fetch: refreshSession, loggedIn } = useUserSession();
const toast = useToast();
const { t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { showPrompt } = useNotificationPrompt();

// Define Zod schema with i18n messages
const schema = z.object({
  email: z.string().email(t('Invalid email')),
  password: z.string().min(6, t('Password must be at least 6 characters'))
});

// Form state
const form = ref({
  email: '',
  password: ''
});
const isLoading = ref(false);
const showPassword = ref(false);

const handleSubmit = async () => {
  await handleLogin(form.value);
};

watchEffect(() => {
  const redirectTarget = resolveAuthenticatedLoginRedirect(loggedIn.value, route.query.redirect);

  if (redirectTarget && import.meta.client) {
    window.location.href = redirectTarget;
  }
});

const handleLogin = async (form: { email: string; password: string }) => {
  try {
    await runLoginFlow({
      getIsLoading: () => isLoading.value,
      setIsLoading: (value) => {
        isLoading.value = value;
      },
      authenticate: () =>
        $fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            Accept: 'application/json; charset=utf-8'
          },
          body: form
        }),
      clearPrivateCaches: clearPrivateRuntimeCaches,
      refreshSession,
      onSuccess: () => {
        toast.add({
          color: 'success',
          title: t('Login successful'),
          description: t('Welcome back')
        });
        showPrompt();
      },
      redirect: () => {
        const redirectTarget = resolveSafeRedirectTarget(route.query.redirect);
        window.location.replace(redirectTarget || localePath('/'));
      }
    });
  } catch (error: unknown) {
    let errorMessage = t('Invalid credentials');

    if (error && typeof error === 'object' && 'statusMessage' in error) {
      errorMessage = error.statusMessage as string;
    }

    toast.add({
      color: 'error',
      title: t('Login failed'),
      description: errorMessage
    });
    console.error('Login error:', error);
  }
};
</script>

<template>
  <div class="flex justify-center">
    <UForm
      class="flex flex-col justify-center p-4 border border-gray-300 rounded-lg shadow-md space-y-2 md:w-96"
      :state="form"
      :schema="schema"
      @submit="handleSubmit"
    >
      <div class="flex mb-6 justify-center">
        <span class="text-lg font-medium">{{ $t('Login') }}</span>
      </div>

      <UFormField :label="$t('Email')" name="email">
        <UInput v-model="form.email" type="email" icon="i-heroicons-envelope" class="w-full" />
      </UFormField>

      <UFormField :label="$t('Password')" name="password">
        <UInput
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          icon="i-heroicons-lock-closed"
          class="w-full"
        >
          <template #trailing>
            <UButton
              color="neutral"
              variant="link"
              size="sm"
              :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
              :aria-label="showPassword ? t('users.form.hidePassword') : t('users.form.showPassword')"
              @click="showPassword = !showPassword"
            />
          </template>
        </UInput>
      </UFormField>

      <div class="flex flex-col space-y-2 mt-4">
        <UButton
          type="submit"
          size="md"
          class="w-full"
          color="primary"
          icon="i-lucide-door-open"
          :loading="isLoading"
          :disabled="isLoading"
        >
          {{ isLoading ? $t('Logging in') : $t('Login') }}
        </UButton>

        <a :href="$localePath('/auth/forgot-password')" class="text-sm text-primary hover:underline text-center">
          {{ $t('Forgot password') }}
        </a>
      </div>
    </UForm>
  </div>
</template>
